import React, {Fragment, useRef, useState} from 'react';
import {Link, useLocation, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import {Popover, Transition} from '@headlessui/react';
import {QrCodeIcon} from "@heroicons/react/24/outline";
import InvoiceLayout from "../layouts/InvoiceLayout";
import ButtonLong from "../components/ButtonLong";
import Loading from "../components/Loading";
import {apollo} from "../utils/apollo";
import {CREATE_DIRECT_PAYMENT_LINK, CREATE_TABBY_PAYMENT_LINK, GET_INVOICE} from "../utils/graphql";
import MoneyHighlight from "../components/MoneyHighlight";
import Captcha from '../components/Captcha'
import {useTranslation} from "react-i18next";
import {cn} from "../utils/style";
import TabbyCheckout, {TabbyLogo} from "../components/TabbyCheckout";
import InvoiceField from "../components/InvoiceField";
import InvoiceBanner from "../components/InvoiceBanner";
import PaymentCallback from "../components/PaymentCallback";
import QRCodePayment from "../components/QRCodePayment";
import satimImage from "../assets/images/satim.png";
import satim3020Image from "../assets/images/3020.svg";
import DirectPayForm from "../components/DirectPayForm";
import {isIframe} from "../utils/iframe";
import {getDeviceFingerprint} from "../utils/fingerprint";
import {CreditCardIcon, LockClosedIcon, SparklesIcon} from "@heroicons/react/24/outline";

function Pay() {
    const {t} = useTranslation();
    const {uuid} = useParams();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [showDirectPay, setShowDirectPay] = useState(false);
    const recaptcha = useRef()
    const conditionsOfUse = useRef()
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const backendMessage = queryParams.get('message') ?? null;
    const callback = queryParams.get('callback') ?? null;

    const fetchInvoice = useQuery(
        ['invoice', {uuid}],
        async () => {
            const {data} = await apollo.query({
                query: GET_INVOICE, variables: {
                    uuid,
                },
            });
            return data?.getInvoice;
        },
        {
            keepPreviousData: false,
            retry: false
        });
    const invoice = fetchInvoice?.data || null;
    const isSatim = invoice?.gatewayCode === 'satim' || false;
    const isDirectGateway = invoice?.gatewayCode?.includes('direct_payment') || false;
    const createDirectPaymentLinkMutation = useMutation({
        mutationFn: async () => {
            const deviceFingerprint = await getDeviceFingerprint();
            const {data} = await apollo.mutate({
                mutation: CREATE_DIRECT_PAYMENT_LINK, variables: {
                    input: {
                        uuid,
                        remarks: "",
                        recaptcha: recaptcha?.current?.getValue() || null,
                        conditions_of_use: conditionsOfUse?.current?.checked || null,
                        device_fingerprint: deviceFingerprint ? {
                            visitor_id: deviceFingerprint.visitor_id,
                            telemetry: deviceFingerprint.telemetry
                        } : null
                    }
                },
            });
            return data?.createDirectPaymentLink;
        },
        onSuccess: async (data) => {
            if (!data) return;
            const {method, url, body, direct_payment} = data;

            // If gateway is a direct payment gateway, always continue with direct pay form
            if (direct_payment === true || isDirectGateway) {
                setShowDirectPay(true);
                return;
            }

            if (method === 'get' && url) {
                if (isIframe()) {
                    window.open(url, '_blank');
                } else {
                    window.location.href = url;
                }
                return;
            }

            if (method === 'post' && url) {

                const parsedData = JSON.parse(body) ?? null;
                if (parsedData) {
                    const form = document.createElement('form');
                    form.method = method;
                    form.name = 'form';
                    form.action = url;
                    if (isIframe()) {
                        form.target = '_blank';
                    }
                    for (const key in parsedData) {
                        if (parsedData.hasOwnProperty(key)) {
                            const hiddenField = document.createElement('input');
                            hiddenField.type = 'hidden';

                            hiddenField.name = key;
                            hiddenField.value = parsedData[key];

                            form.appendChild(hiddenField);
                        }
                    }
                    document.body.appendChild(form);
                    try {
                        form.submit();
                    } catch (err) {
                        HTMLFormElement.prototype.submit.call(form);
                    }
                    return;
                }
            }

            alert(t('pay.unable_to_generate_link'));
        },
        onSettled: async () => {
            recaptcha?.current?.reset()
        }
    });

    const createTabbyPaymentLinkMutation = useMutation({
        mutationFn: async () => {
            const deviceFingerprint = await getDeviceFingerprint();
            const {data} = await apollo.mutate({
                mutation: CREATE_TABBY_PAYMENT_LINK,
                variables: {
                    input: {
                        uuid,
                        address: address || invoice?.address || null,
                        city: city || invoice?.city || null,
                        zip_code: zipCode || invoice?.zipCode || null,
                        recaptcha: recaptcha?.current?.getValue() || null,
                        conditions_of_use: conditionsOfUse?.current?.checked || null,
                        device_fingerprint: deviceFingerprint ? {
                            visitor_id: deviceFingerprint.visitor_id,
                            telemetry: deviceFingerprint.telemetry
                        } : null
                    }
                }
            });
            return data?.createTabbyPaymentLink;
        },
        onSuccess: async (data) => {
            if (!data) return;
            const {url} = data;
            if (url) {
                if (isIframe()) {
                    window.open(url, '_blank');
                } else {
                    window.location.href = url;
                }
                return;
            }
            alert(t('pay.unable_to_generate_link'));
        },
        onSettled: async () => {
            recaptcha?.current?.reset();
        }
    });

    const isGeneratingLink = createDirectPaymentLinkMutation.isLoading || createTabbyPaymentLinkMutation.isLoading;

    if (fetchInvoice.isLoading) return (<InvoiceLayout invoice={invoice}>
        <Loading message={t('_.loading')} className="mt-20" />
    </InvoiceLayout>);

    const handlePayButton = () => {
        if (selectedPaymentMethod === 'tabby') {
            createTabbyPaymentLinkMutation.mutate();
        } else {
            createDirectPaymentLinkMutation.mutate();
        }
    };

    if (fetchInvoice.isError) {
        return <InvoiceLayout invoice={invoice}><InvoiceBanner backendMessage={backendMessage} status="unknown_error"/></InvoiceLayout>
    }

    if (fetchInvoice.isSuccess && !invoice) {
        return <InvoiceLayout invoice={invoice}><InvoiceBanner backendMessage={backendMessage}
                                                               status="not_found"/></InvoiceLayout>
    }

    if (fetchInvoice.isSuccess && invoice && callback && ['completed', 'failed', 'canceled'].includes(invoice?.status)) {
        if (isIframe()) {
            try {
                window.parent.postMessage({
                    type: 'PAYMENT_STATUS',
                    status: invoice.status,
                    uuid: invoice.uuid
                }, '*');
            } catch (e) {
                console.error('Failed to post message to parent', e);
            }
        }
        return <PaymentCallback backendMessage={backendMessage} invoice={invoice}/>;
    }

    return (<InvoiceLayout invoice={invoice} test={invoice?.test} showLang={true}>
        {isGeneratingLink && <div
            className="z-50 backdrop-blur-xl w-full h-full fixed top-0 left-0 bg-white/40 flex justify-center items-center animate-fade-in">
            <Loading message={selectedPaymentMethod === 'tabby' ? t('pay.redirecting', 'Redirecting ...') : t('pay.redirecting')} fullHeight={false} />
        </div>}

        <InvoiceBanner backendMessage={backendMessage} status={invoice?.status} antiFraud={invoice?.antiFraud}/>

        {showDirectPay ? (
            <DirectPayForm uuid={uuid} invoice={invoice} onBack={() => setShowDirectPay(false)} />
        ) : (
            <>
            <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/50 mb-6 mt-3 border-0 overflow-hidden">
            <div
                className="px-8 py-10 bg-gradient-to-br from-[#0b5f82] to-[#0b9cbc] border-0">
                <div className="flex flex-col items-center text-center gap-4">
                    <div className="p-4 rounded-3xl bg-white/10 backdrop-blur-md shadow-inner">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" viewBox="0 0 20 20"
                             fill="currentColor">
                            <path
                                d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
                        </svg>
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-white tracking-tight">
                            {t('invoice.payment_information')}
                        </h2>
                        <p className="text-cyan-100/80 text-sm font-medium mt-1">{t('pay.secure_checkout_experience')}</p>
                    </div>
                </div>
            </div>

            <div className="p-6 sm:p-10">
                <div className="flex items-center mb-6">
                    <div className="bg-cyan-50 p-2 rounded-xl mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#0b6a8c]" viewBox="0 0 20 20"
                             fill="currentColor">
                            <path fillRule="evenodd"
                                  d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 100-12 6 6 0 000 12zm.75-6a.75.75 0 000-1.5h-1.5a.75.75 0 000 1.5h1.5z"
                                  clipRule="evenodd"/>
                        </svg>
                    </div>
                    <div className="flex items-center justify-between w-full">
                        <h3 className="text-base font-bold text-gray-900 tracking-tight">
                            {t('invoice.merchant_information')}
                        </h3>
                        {['pending', 'failed'].includes(invoice?.status) && (
                            <Popover className="relative">
                                <Popover.Button
                                    className="p-1.5 rounded-lg bg-white border border-gray-200 shadow-sm hover:border-cyan-300 hover:text-[#0b6a8c] transition-all group"
                                    title={t('pay.scan_to_pay')}
                                >
                                    <QrCodeIcon className="h-5 w-5 text-gray-500 group-hover:text-[#0b6a8c]" />
                                </Popover.Button>
                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-200"
                                    enterFrom="opacity-0 translate-y-1"
                                    enterTo="opacity-100 translate-y-0"
                                    leave="transition ease-in duration-150"
                                    leaveFrom="opacity-100 translate-y-0"
                                    leaveTo="opacity-0 translate-y-1"
                                >
                                    <Popover.Panel className="absolute ltr:right-0 rtl:left-0 z-20 mt-2 w-72">
                                        <QRCodePayment
                                            paymentUrl={window.location.href}
                                            title={t('pay.scan_to_pay')}
                                            className="shadow-2xl ring-1 ring-black ring-opacity-5 border-0 !block"
                                        />
                                    </Popover.Panel>
                                </Transition>
                            </Popover>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 mb-10">
                    {invoice?.supplier?.displaySupplierDetails ? <>
                        <InvoiceField name={t('invoice.merchant')} value={invoice?.supplier?.businessName}
                                      className="col-span-12 sm:col-span-6 bg-[#0b9cbc]/5 border-0"/>
                        <InvoiceField name={t('invoice.email')} value={invoice?.supplier?.email}
                                      className="col-span-12 sm:col-span-6"/>
                        <InvoiceField name={t('invoice.mobile')} value={invoice?.supplier?.phone}
                                      className="col-span-12 sm:col-span-6"/>
                    </> : (invoice?.merchant?.hideMerchantDetails ? <>
                        <InvoiceField name={t('invoice.merchant')} value={invoice?.merchant?.merchantName}
                                      className="col-span-12 bg-[#0b9cbc]/5 border-0"/>
                    </> : <>
                        <InvoiceField name={t('invoice.merchant')} value={invoice?.merchant?.merchantName}
                                      className="col-span-12 sm:col-span-6 bg-[#0b9cbc]/5 border-0"/>
                        <InvoiceField name={t('invoice.address')} value={invoice?.merchant?.merchantAddress}
                                      className="col-span-12 sm:col-span-6"/>
                        <InvoiceField name={t('invoice.email')} value={invoice?.merchant?.merchantEmail}
                                      className="col-span-12 sm:col-span-6"/>
                        <InvoiceField name={t('invoice.mobile')} value={invoice?.merchant?.merchantPhone}
                                      className="col-span-12 sm:col-span-6"/>
                    </>)}
                </div>

                <div className="flex items-center mb-6">
                    <div className="bg-cyan-50 p-2 rounded-xl mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#0b6a8c]" viewBox="0 0 20 20"
                             fill="currentColor">
                            <path fillRule="evenodd"
                                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                  clipRule="evenodd"/>
                        </svg>
                    </div>
                    <h3 className="text-base font-bold text-gray-900 tracking-tight">
                        {t('invoice.invoice_details')}
                    </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                    <InvoiceField
                        name={t('invoice.amount')}
                        value={
                            <div className="flex items-center">
                                <span className="text-xl font-black text-[#0b5f82]">
                                    {invoice?.totalAmount || ''} {invoice?.currency?.symbol || ''}
                                </span>
                            </div>
                        }
                        className="col-span-12 sm:col-span-4 bg-cyan-50/50"
                    />

                    <InvoiceField
                        name={t('invoice.reference_number')}
                        value={invoice?.displayReferenceNumber || invoice?.customerReferenceNumber}
                        className="col-span-12 sm:col-span-4"
                    />

                    <InvoiceField
                        name={t('invoice.date')}
                        value={invoice?.createdAt}
                        className="col-span-12 sm:col-span-4"
                    />

                    <div className="col-span-12 bg-gray-50/30 rounded-3xl p-6 border-0 mt-2">
                        <div className="text-[10px] font-bold text-gray-400 mb-4 uppercase tracking-widest">{t('invoice.customer_information')}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                            <InvoiceField name={t('invoice.name')} value={invoice?.name}
                                          className="col-span-12 sm:col-span-4 bg-white border-0 shadow-sm"/>
                            <InvoiceField name={t('invoice.phone')} value={invoice?.phone}
                                          className="col-span-12 sm:col-span-4 bg-white border-0 shadow-sm"/>
                            <InvoiceField name={t('invoice.email')} value={invoice?.email}
                                          className="col-span-12 sm:col-span-4 bg-white border-0 shadow-sm"/>
                        </div>
                    </div>

                    {isSatim &&
                        <InvoiceField
                            name={t('pay.le_mode_de_paiement')}
                            value={
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold text-gray-700">{t('pay.cib_edahabia')}</span>
                                    <img src={satimImage} className="h-6" alt={t('pay.cib_edahabia')}/>
                                </div>
                            }
                            className="col-span-12 sm:col-span-8 bg-cyan-50/50 border-0"
                        />
                    }
                </div>
            </div>
        </div>

        {!!invoice?.products?.length && (
            <div className="overflow-hidden rounded-[2rem] shadow-lg border-0 bg-white mb-6">
                <div className="px-8 py-6 border-b border-gray-50 flex items-center justify-between">
                    <h3 className="text-base font-bold text-gray-900 tracking-tight">{t('invoice.items_services')}</h3>
                    <span className="bg-cyan-50 text-[#0b5f82] text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full">{invoice?.products?.length} {t('invoice.items')}</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                        <thead>
                        <tr className="bg-gray-50/50">
                            <th scope="col"
                                className="py-4 pl-8 pr-4 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                                {t('invoice.product')}
                            </th>
                            <th scope="col"
                                className="px-4 py-4 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                                {t('invoice.amount')}
                            </th>
                            <th scope="col"
                                className="px-4 py-4 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                                {t('invoice.vat')}
                            </th>
                            <th scope="col"
                                className="px-4 py-4 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                                {t('invoice.quantity')}
                            </th>
                            <th scope="col"
                                className="px-4 py-4 pr-8 text-right text-[10px] font-black uppercase tracking-widest text-gray-400">
                                {t('invoice.total')}
                            </th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                        {invoice?.products?.map((product, index) => (
                            <tr
                                key={product.id}
                                className="transition-all duration-300 hover:bg-gray-50/50 group"
                            >
                                <td className="py-5 pl-8 pr-4 text-sm font-bold text-gray-900">
                                    {product.title}
                                    <div className="text-gray-400 font-medium text-xs mt-0.5">{product.details}</div>
                                </td>
                                <td className="px-4 py-5 text-sm font-semibold text-gray-600">{product.amount} {invoice?.currency?.symbol}</td>
                                <td className="px-4 py-5 text-sm font-semibold text-gray-600">{product.vat === 0 ? '—' : product.vat}</td>
                                <td className="px-4 py-5 text-sm">
                                    <span className="bg-gray-100 text-gray-600 font-bold px-2.5 py-1 rounded-lg text-xs">× {product.quantity}</span>
                                </td>
                                <td className="px-4 py-5 pr-8 text-right text-sm font-black text-[#0b5f82]">{product.total} {invoice?.currency?.symbol || ''}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )}


        {/* Payment Method Selector (Available when merchant has Tabby enabled) */}
        {invoice?.isTabbyAvailable && ['pending', 'failed'].includes(invoice?.status) && (
            <div className="overflow-hidden rounded-[2rem] shadow-lg border-0 bg-white mb-6 animate-fade-in">
                <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between">
                    <h3 className="text-base font-bold text-gray-900 tracking-tight">
                        {t('pay.select_payment_method', 'Select Payment Method')}
                    </h3>
                    <span className="bg-emerald-50 text-emerald-800 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border border-emerald-200/60 flex items-center gap-1">
                        <SparklesIcon className="h-3 w-3 text-emerald-600" />
                        {t('pay.tabby_zero_interest', '0% Interest available')}
                    </span>
                </div>
                <div className="p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Option 1: Card */}
                    <button
                        type="button"
                        onClick={() => setSelectedPaymentMethod('card')}
                        className={cn(
                            "relative flex flex-col p-5 rounded-2xl border-2 text-left transition-all duration-200 group cursor-pointer",
                            selectedPaymentMethod === 'card'
                                ? "border-[#0b9cbc] bg-cyan-50/40 shadow-md shadow-[#0b9cbc]/10 ring-2 ring-[#0b9cbc]/20"
                                : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50"
                        )}
                    >
                        <div className="flex items-center justify-between w-full mb-3">
                            <div className="p-2.5 rounded-xl bg-cyan-100/60 text-[#0b5f82] group-hover:scale-105 transition-transform">
                                <CreditCardIcon className="h-6 w-6" />
                            </div>
                            <div className={cn(
                                "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
                                selectedPaymentMethod === 'card' ? "border-[#0b9cbc] bg-[#0b9cbc]" : "border-gray-300"
                            )}>
                                {selectedPaymentMethod === 'card' && (
                                    <div className="w-2 h-2 rounded-full bg-white" />
                                )}
                            </div>
                        </div>
                        <div className="font-black text-gray-900 text-sm">
                            {t('pay.card_payment', 'Debit / Credit Card')}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 font-medium leading-relaxed">
                            {t('pay.card_payment_desc', 'Pay securely using your Visa or Mastercard')}
                        </div>
                    </button>

                    {/* Option 2: Tabby */}
                    <button
                        type="button"
                        onClick={() => setSelectedPaymentMethod('tabby')}
                        className={cn(
                            "relative flex flex-col p-5 rounded-2xl border-2 text-left transition-all duration-200 group cursor-pointer",
                            selectedPaymentMethod === 'tabby'
                                ? "border-emerald-500 bg-emerald-50/50 shadow-md shadow-emerald-500/10 ring-2 ring-emerald-500/20"
                                : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50"
                        )}
                    >
                        <div className="flex items-center justify-between w-full mb-3">
                            <div className="bg-white rounded-xl px-2.5 py-1 shadow-sm border border-emerald-100 group-hover:scale-105 transition-transform">
                                <TabbyLogo className="h-5 w-auto" />
                            </div>
                            <div className={cn(
                                "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
                                selectedPaymentMethod === 'tabby' ? "border-emerald-600 bg-emerald-600" : "border-gray-300"
                            )}>
                                {selectedPaymentMethod === 'tabby' && (
                                    <div className="w-2 h-2 rounded-full bg-white" />
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="font-black text-gray-900 text-sm">
                                {t('pay.tabby_payment', 'Tabby - Split in 4')}
                            </span>
                            <span className="bg-emerald-100 text-emerald-800 text-[9px] font-black uppercase px-2 py-0.5 rounded-full">
                                {t('pay.tabby_zero_interest', '0% Interest')}
                            </span>
                        </div>
                        <div className="text-xs text-emerald-900/80 mt-1 font-medium leading-relaxed">
                            {t('pay.tabby_payment_desc', '4 interest-free payments. No hidden fees.')}
                        </div>
                    </button>
                </div>
            </div>
        )}

        {/* Tabby Checkout Details Component */}
        {invoice?.isTabbyAvailable && selectedPaymentMethod === 'tabby' && ['pending', 'failed'].includes(invoice?.status) && (
            <TabbyCheckout
                invoice={invoice}
                address={address}
                setAddress={setAddress}
                city={city}
                setCity={setCity}
                zipCode={zipCode}
                setZipCode={setZipCode}
                className="mb-6"
            />
        )}

        <div
            className="sticky bottom-4 left-0 w-full bg-white/80 backdrop-blur-xl py-6 px-8 border-0 shadow-2xl shadow-blue-900/10 rounded-[2.5rem] mt-8">
            {(['pending', 'failed'].includes(invoice?.status) && isSatim) && (
                <div className="bg-cyan-50/50 p-6 rounded-3xl mb-6 border-0">
                    <div className="flex mb-4 overflow-hidden rounded-2xl shadow-inner bg-white">
                        <Captcha ref={recaptcha}/>
                    </div>
                    <div className="flex gap-x-3 items-center">
                        <div className="relative flex items-center">
                            <input
                                ref={conditionsOfUse}
                                type="checkbox"
                                name="conditionsOfUse"
                                id="conditionsOfUse"
                                className="h-5 w-5 rounded-lg border-gray-200 text-[#0b9cbc] focus:ring-[#0b9cbc]/30 transition-all cursor-pointer"
                            />
                        </div>
                        <div className="flex flex-wrap items-center gap-x-1">
                            <label htmlFor="conditionsOfUse" className="select-none text-gray-600 text-sm font-medium cursor-pointer">{t('pay.accept_conditions')}</label>
                            <Link to="/conditions-of-use-satim" target="_blank"
                                  className="font-bold text-[#0b9cbc] hover:text-[#0b5f82] transition-colors text-sm underline decoration-2 underline-offset-4">
                                {t('pay.conditions_of_use')}
                            </Link>
                        </div>
                    </div>
                </div>
            )}

                <div className={cn(
                    'flex flex-col md:flex-row mb-6 px-1',
                    {
                        'gap-x-2 items-end': isSatim,
                        'justify-between items-center': !isSatim,
                    }
                )}>
                    <div>
                        <span className="text-gray-400 font-bold uppercase tracking-widest text-xs">{t('pay.total_payable_amount')}</span>
                        {invoice?.isTabbyAvailable && selectedPaymentMethod === 'tabby' && (
                            <div className="text-xs font-bold text-emerald-700 mt-0.5">
                                {t('pay.due_today', 'Due today')}: {(parseFloat(invoice?.totalAmount || 0) / 4).toFixed(2)} {invoice?.currency?.symbol}
                            </div>
                        )}
                    </div>
                    <MoneyHighlight
                        value={invoice?.totalAmount}
                        currency={invoice?.currency?.symbol}
                        className="!text-3xl !font-black !text-[#0b5f82]"
                    />
                </div>

            {['pending', 'failed'].includes(invoice?.status) && (
                <div className="flex gap-2">
                    {invoice?.isTabbyAvailable && selectedPaymentMethod === 'tabby' ? (
                        <button
                            type="button"
                            onClick={handlePayButton}
                            disabled={isGeneratingLink}
                            className="w-full relative inline-flex items-center justify-center gap-3 px-8 py-4 text-base font-black text-slate-950 bg-gradient-to-r from-emerald-400 via-teal-300 to-emerald-400 hover:from-emerald-300 hover:to-teal-200 rounded-2xl shadow-xl shadow-emerald-500/25 transition-all duration-300 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                            <TabbyLogo className="h-6 w-auto" />
                            <span>{t('pay.pay_with_tabby', 'Pay with Tabby')}</span>
                            <span className="text-xs font-bold bg-slate-950/10 text-slate-950 px-2.5 py-1 rounded-lg">
                                4 × {(parseFloat(invoice?.totalAmount || 0) / 4).toFixed(2)} {invoice?.currency?.symbol}
                            </span>
                        </button>
                    ) : (
                        <ButtonLong
                            onClick={handlePayButton}
                            disabled={isGeneratingLink}
                            className="group"
                        >
                            <LockClosedIcon className="h-5 w-5 text-white/80 group-hover:text-white transition-colors" />
                            <div className="flex items-center gap-2">
                                {isSatim && <img src={satimImage} className="h-7 brightness-0 invert" alt={t('pay.cib_edahabia')}/>}
                                <span>{t('pay.pay_now')}</span>
                            </div>
                        </ButtonLong>
                    )}
                </div>
            )}

            {isSatim && (
                <div
                    className="mt-4 p-4 bg-gray-50 rounded-2xl flex items-center text-gray-500 justify-between border-0 shadow-inner">
                    <div className="text-xs font-medium tracking-wide">{t('pay.satim_free_toll')}</div>
                    <img
                        className="shrink-0 w-20 opacity-80"
                        src={satim3020Image}
                        alt="SATIM 3020"
                    />
                </div>
            )}
        </div>
            </>
        )}
    </InvoiceLayout>);
}

export default Pay;
